import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CssBaseline,
  Grid,
  Link,
  Sheet,
  Table,
  ThemeProvider,
  Typography,
  Alert,
} from '@mui/joy';
import { theme } from '../../main/theme/constants';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import {
  KeyboardArrowRight,
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Cancel as CancelIcon,
  DoneAll as DoneAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WarningAmber as WarningAmberIcon,
  LinkOff as LinkOffIcon,
  Inventory2 as Inventory2Icon,
} from '@mui/icons-material';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import { postShipmentsUploadPreview, postShipmentsUploadConfirm, getShipmentsUploadTemplate, getShipmentsUploadSample } from '../../main/API/shipment';
import { OmrAmount } from '../../main/components/OmrAmount';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { formatPaymentStatus, getPaymentStatusChipStyle } from '../../main/widgets/PaymentStatusOptionList';

// Preview-only fields returned by API for display; excluded from column list (shown as "id (Name)" in id column).
const PREVIEW_ONLY_DISPLAY_KEYS = ['owner_name', 'location_name', 'type_name', 'service_names', 'shipment_unit_serial_number'];
// Not shown as auto columns in import preview (system-set or rendered separately).
// `pieces` is rendered as an expandable sub-row, not as a regular cell.
const PREVIEW_HIDDEN_KEYS = ['invoiced', 'pieces', 'shipment_unit_id'];

// Preferred column order for preview (matches Excel template); any extra keys appear after.
const PREFERRED_PREVIEW_KEYS = [
  'external_id',
  'shipper_reference',
  'tracking_number',
  'items_description',
  'packages_quantity',
  'weight',
  'width',
  'length',
  'height',
  'volumetric_weight',
  'charge',
  'price',
  'auto_calculating_price',
  'owner_id',
  'location_id',
  'current_location',
  'note',
  'transaction_date',
  'status',
  'paid',
  'payment_status',
  'type_id',
  'shipment_unit_number',
  'pricing_tariff_code',
  'product_category_code',
  'meter_price_override',
  'extra_cost',
  'service_ids',
  'tag_names',
];

// Classify the row_errors map returned by the preview endpoint.
// Backend uses string keys with prefixes so different error scopes don't collide:
//   "<index>"                  -> blocking shipment-row error (excluded from preview)
//   "pieces:<index>"           -> blocking piece-row error (parent shipment still imported)
//   "warning:<index>"          -> non-blocking weight-mismatch warning on a shipment row
//   "orphan_pieces:<external>" -> Pieces sheet references a missing Shipments row
function classifyRowErrors(rowErrors) {
  const shipmentErrors = [];
  const pieceErrors = [];
  const warnings = [];
  const orphans = [];
  if (!rowErrors || typeof rowErrors !== 'object') {
    return { shipmentErrors, pieceErrors, warnings, orphans };
  }
  for (const [key, message] of Object.entries(rowErrors)) {
    if (key.startsWith('warning:')) {
      const rowIndex = Number(key.slice('warning:'.length));
      warnings.push({ rowIndex, message });
    } else if (key.startsWith('orphan_pieces:')) {
      const externalId = key.slice('orphan_pieces:'.length);
      orphans.push({ externalId, message });
    } else if (key.startsWith('pieces:')) {
      const rowIndex = Number(key.slice('pieces:'.length));
      pieceErrors.push({ rowIndex, message });
    } else {
      const rowIndex = Number(key);
      shipmentErrors.push({ rowIndex: Number.isFinite(rowIndex) ? rowIndex : key, message });
    }
  }
  return { shipmentErrors, pieceErrors, warnings, orphans };
}

function getPreviewColumns(preview) {
  if (!preview || preview.length === 0) return [];
  const keySet = new Set();
  preview.forEach((row) => Object.keys(row).forEach((k) => keySet.add(k)));
  keySet.delete('row_index');
  PREVIEW_ONLY_DISPLAY_KEYS.forEach((k) => keySet.delete(k));
  PREVIEW_HIDDEN_KEYS.forEach((k) => keySet.delete(k));
  const ordered = [...keySet].sort((a, b) => {
    const ai = PREFERRED_PREVIEW_KEYS.indexOf(a);
    const bi = PREFERRED_PREVIEW_KEYS.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return String(a).localeCompare(String(b));
  });
  return ordered;
}

function previewColumnWidth(key) {
  const widths = {
    external_id: { minWidth: 100, width: 110 },
    shipper_reference: { minWidth: 100, width: 110 },
    tracking_number: { minWidth: 120, width: 130 },
    items_description: { minWidth: 140, width: 180 },
    packages_quantity: { minWidth: 56, width: 64 },
    weight: { minWidth: 64, width: 72 },
    width: { minWidth: 56, width: 64 },
    length: { minWidth: 56, width: 64 },
    height: { minWidth: 56, width: 64 },
    width_cm: { minWidth: 56, width: 64 },
    length_cm: { minWidth: 56, width: 64 },
    height_cm: { minWidth: 56, width: 64 },
    volumetric_weight: { minWidth: 72, width: 88 },
    charge: { minWidth: 88, width: 96 },
    price: { minWidth: 88, width: 96 },
    auto_calculating_price: { minWidth: 72, width: 88 },
    owner_id: { minWidth: 100, width: 140 },
    location_id: { minWidth: 100, width: 140 },
    location: { minWidth: 80, width: 88 },
    current_location: { minWidth: 100, width: 120 },
    note: { minWidth: 100, width: 140 },
    transaction_date: { minWidth: 100, width: 120 },
    status: { minWidth: 100, width: 120 },
    paid: { minWidth: 56, width: 64 },
    payment_status: { minWidth: 100, width: 120 },
    deleted: { minWidth: 56, width: 64 },
    type_id: { minWidth: 100, width: 140 },
    type: { minWidth: 72, width: 88 },
    shipment_unit_number: { minWidth: 120, width: 150 },
    service_ids: { minWidth: 180, width: 260 },
    tag_names: { minWidth: 140, width: 220 },
    pricing_tariff_code: { minWidth: 88, width: 100 },
    product_category_code: { minWidth: 100, width: 120 },
    meter_price_override: { minWidth: 100, width: 120 },
    extra_cost: { minWidth: 88, width: 100 },
  };
  return widths[key] || { minWidth: 88, width: 100 };
}

function formatNumeric(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, '');
}

function humanizeFieldKey(key) {
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function previewColumnLabel(key, t) {
  return t(`importShipments.previewCol.${key}`, { defaultValue: humanizeFieldKey(key) });
}

function formatPreviewCellValue(key, value, row = {}, t) {
  if (key === 'external_id') {
    if (value == null || value === '') return '—';
    return String(value);
  }
  if (key === 'owner_id') {
    if (value == null && !row.owner_name) return '—';
    const name = row.owner_name != null && row.owner_name !== '' ? row.owner_name : null;
    return name != null ? `${value} (${name})` : (value != null ? String(value) : '—');
  }
  if (key === 'location_id' || key === 'location') {
    const id = key === 'location_id' ? value : value;
    const name = row.location_name != null && row.location_name !== '' ? row.location_name : null;
    if (id == null && !name) return '—';
    return name != null ? `${id} (${name})` : (id != null ? String(id) : '—');
  }
  if (key === 'type_id' || key === 'type') {
    const id = value;
    const name = row.type_name != null && row.type_name !== '' ? row.type_name : null;
    if (id == null && !name) return '—';
    return name != null ? `${id} (${name})` : (id != null ? String(id) : '—');
  }
  if (key === 'shipment_unit_number') {
    if (value == null || value === '') return '—';
    return String(value);
  }
  if (key === 'service_ids') {
    const ids = Array.isArray(value) ? value : [];
    const names = Array.isArray(row.service_names) ? row.service_names : [];
    if (ids.length === 0) return '—';
    const parts = ids.map((id, i) => (names[i] != null && names[i] !== '' ? `${id} (${names[i]})` : String(id)));
    return parts.join(', ');
  }
  if (key === 'tag_names') {
    const names = Array.isArray(value) ? value : [];
    if (names.length === 0) return '—';
    return names.filter((n) => n != null && String(n).trim() !== '').join(', ');
  }
  if (key === 'transaction_date') {
    if (value == null || value === '') return '—';
    const s = String(value);
    return s.length >= 10 ? s.slice(0, 10) : s;
  }
  if (value == null || value === '') return '—';
  if (key === 'status') return formatStatus(value, t);
  if (key === 'payment_status') return formatPaymentStatus(value);
  if (key === 'price' || key === 'charge' || key === 'meter_price_override' || key === 'extra_cost') {
    return <OmrAmount value={value} />;
  }
  if (key === 'deleted' || key === 'auto_calculating_price' || key === 'paid') {
    const b = value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
    return b ? t('actions.yes') : t('actions.no');
  }
  if (key === 'items_description') {
    return value;
  }
  return String(value);
}

function formatStatus(status, t) {
  if (!status) return '—';
  const key = String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const withSpaces = key.replace(/_/g, ' ');
  const map = {
    shipping_soon: 'widgets.shipmentStatusShippingSoon',
    'shipping soon': 'widgets.shipmentStatusShippingSoon',
    shipped: 'widgets.shipmentStatusShipped',
    arrived: 'widgets.shipmentStatusArrived',
    out_for_delivery: 'widgets.shipmentStatusOutForDelivery',
    'out for delivery': 'widgets.shipmentStatusOutForDelivery',
    delivered: 'widgets.shipmentStatusDelivered',
  };
  const labelKey = map[key] || map[withSpaces];
  return labelKey ? t(labelKey) : status;
}

function shipmentStatusChipStyle(status) {
  const styles = {
    shipping_soon: { bg: '#f1f5f9', color: '#64748b' },
    'shipping soon': { bg: '#f1f5f9', color: '#64748b' },
    shipped: { bg: '#eef2ff', color: '#4f46e5' },
    arrived: { bg: '#eff6ff', color: '#2563eb' },
    out_for_delivery: { bg: '#fffbeb', color: '#d97706' },
    'out for delivery': { bg: '#fffbeb', color: '#d97706' },
    delivered: { bg: '#ecfdf5', color: '#059669' },
  };
  const key = status && String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const withSpaces = key && key.replace(/_/g, ' ');
  return styles[key] || styles[withSpaces] || { bg: '#f1f5f9', color: '#64748b' };
}

const pageBg = '#f0f4f8';
const cardBg = '#ffffff';
const cardBorder = '1px solid #e2e8f0';
const cardShadow = '0 2px 8px rgba(0,0,0,0.06)';
const cardRadius = '16px';
const textPrimary = '#1e293b';
const textSecondary = '#64748b';

function isExcelFile(f) {
  if (!f || !f.name) return false;
  const lower = f.name.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return true;
  const t = f.type || '';
  return (
    t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    t === 'application/vnd.ms-excel'
  );
}

export default function ImportShipmentsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canUploadExcel = hasPermission(PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL);
  const [file, setFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState(null);
  const [inputKey, setInputKey] = useState(Date.now());
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef(null);

  // Step 1 result
  const [preview, setPreview] = useState([]);
  const [globalErrors, setGlobalErrors] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'done'

  // Selection: set of row_index (number) for preview items
  const [selectedRowIndices, setSelectedRowIndices] = useState(new Set());
  // Pieces sub-row expansion state
  const [expandedRowIndices, setExpandedRowIndices] = useState(new Set());

  // Step 2 result
  const [confirmResult, setConfirmResult] = useState(null); // { detail, created_count, errors }

  // Categorize row_errors once per change.
  const classifiedRowErrors = useMemo(() => classifyRowErrors(rowErrors), [rowErrors]);
  const warningByShipmentRow = useMemo(() => {
    const map = new Map();
    classifiedRowErrors.warnings.forEach((w) => map.set(w.rowIndex, w.message));
    return map;
  }, [classifiedRowErrors]);

  const applyExcelFile = (nextFile) => {
    if (!nextFile || !isExcelFile(nextFile)) return;
    setFile(nextFile);
    setConfirmResult(null);
    setStep('upload');
  };

  const handleFileChange = (event) => {
    const next = event.target.files && event.target.files[0];
    applyExcelFile(next);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (dropped && isExcelFile(dropped)) {
      applyExcelFile(dropped);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateError(null);
    setTemplateLoading(true);
    try {
      const response = await getShipmentsUploadTemplate();
      setTemplateLoading(false);
      if (response.status !== 200 || !response.data) {
        setTemplateError(t('importShipments.errorTemplateDownload'));
        return;
      }
      const blob = response.data;
      const disposition = response.headers && response.headers['content-disposition'];
      let filename = 'shipment_import_template.xlsx';
      if (disposition && typeof disposition === 'string') {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1].trim();
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      setTemplateLoading(false);
      setTemplateError(err.response && err.response.data ? (typeof err.response.data === 'string' ? err.response.data : (err.response.data.detail || err.response.data.error)) : t('importShipments.errorTemplateDownload'));
    }
  };

  const handleDownloadSample = async () => {
    setSampleError(null);
    setSampleLoading(true);
    try {
      const response = await getShipmentsUploadSample();
      setSampleLoading(false);
      if (response.status !== 200 || !response.data) {
        setSampleError(t('importShipments.errorSampleDownload'));
        return;
      }
      const blob = response.data;
      const disposition = response.headers && response.headers['content-disposition'];
      let filename = 'shipment_import_sample_5_rows.xlsx';
      if (disposition && typeof disposition === 'string') {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1].trim();
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      setSampleLoading(false);
      setSampleError(err.response && err.response.data ? (typeof err.response.data === 'string' ? err.response.data : (err.response.data.detail || err.response.data.error)) : t('importShipments.errorSampleDownload'));
    }
  };

  const handleUploadPreview = async (event) => {
    event.preventDefault();
    if (!file) return;
    if (!canUploadExcel) {
      setGlobalErrors([t('common.permissionDenied', { defaultValue: 'Permission denied.' })]);
      return;
    }
    setUploadLoading(true);
    setGlobalErrors([]);
    setRowErrors({});
    setPreview([]);
    setConfirmResult(null);
    setExpandedRowIndices(new Set());
    try {
      const response = await postShipmentsUploadPreview(file);
      setUploadLoading(false);
      if (response.status === 200) {
        const data = response.data || {};
        setGlobalErrors(Array.isArray(data.errors) ? data.errors : []);
        setRowErrors(typeof data.row_errors === 'object' && data.row_errors !== null ? data.row_errors : {});
        const prev = Array.isArray(data.preview) ? data.preview : [];
        setPreview(prev);
        setSelectedRowIndices(new Set(prev.map((p) => p.row_index)));
        // Auto-expand any shipment that has explicit pieces[] so users see them.
        setExpandedRowIndices(
          new Set(
            prev
              .filter((p) => Array.isArray(p.pieces) && p.pieces.length > 0)
              .map((p) => p.row_index)
          )
        );
        setStep('preview');
      } else {
        setGlobalErrors([(response.data && (response.data.error || response.data.detail)) || t('importShipments.uploadFailed')]);
      }
    } catch (err) {
      setUploadLoading(false);
      const msg = err.response && err.response.data && (err.response.data.error || err.response.data.detail);
      setGlobalErrors([msg || t('importShipments.uploadErrorGeneric')]);
    }
  };

  const handleConfirmImport = async () => {
    const toSend = preview.filter((p) => selectedRowIndices.has(p.row_index));
    if (toSend.length === 0) return;
    if (!canUploadExcel) {
      setGlobalErrors([t('common.permissionDenied', { defaultValue: 'Permission denied.' })]);
      return;
    }
    setConfirmLoading(true);
    setConfirmResult(null);
    try {
      const response = await postShipmentsUploadConfirm(toSend);
      setConfirmLoading(false);
      if (response.status === 200) {
        const data = response.data || {};
        setConfirmResult({
          detail: data.detail || t('importShipments.importedCountDetail', { count: data.created_count != null ? data.created_count : 0 }),
          created_count: data.created_count,
          errors: Array.isArray(data.errors) ? data.errors : [],
        });
        setStep('done');
      } else {
        setConfirmResult({
          detail: (response.data && (response.data.error || response.data.detail)) || t('importShipments.importFailed'),
          created_count: 0,
          errors: [],
        });
      }
    } catch (err) {
      setConfirmLoading(false);
      const msg = err.response && err.response.data && (err.response.data.error || err.response.data.detail);
      setConfirmResult({ detail: msg || t('importShipments.confirmImportError'), created_count: 0, errors: [] });
    }
  };

  const allSelected = preview.length > 0 && preview.every((p) => selectedRowIndices.has(p.row_index));
  const someSelected = selectedRowIndices.size > 0;
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRowIndices(new Set());
    } else {
      setSelectedRowIndices(new Set(preview.map((p) => p.row_index)));
    }
  };
  const toggleSelectRow = (rowIndex) => {
    setSelectedRowIndices((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };
  const toggleExpandRow = (rowIndex) => {
    setExpandedRowIndices((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };
  const expandableRowIndices = useMemo(
    () =>
      preview
        .filter((p) => Array.isArray(p.pieces) && p.pieces.length > 0)
        .map((p) => p.row_index),
    [preview]
  );
  const allExpanded =
    expandableRowIndices.length > 0 &&
    expandableRowIndices.every((idx) => expandedRowIndices.has(idx));
  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedRowIndices(new Set());
    } else {
      setExpandedRowIndices(new Set(expandableRowIndices));
    }
  };

  const startOver = () => {
    dragDepthRef.current = 0;
    setDragActive(false);
    setFile(null);
    setInputKey(Date.now());
    setPreview([]);
    setGlobalErrors([]);
    setRowErrors({});
    setSelectedRowIndices(new Set());
    setExpandedRowIndices(new Set());
    setConfirmResult(null);
    setStep('upload');
  };

  const structureRows = useMemo(
    () => [
      { id: 0, fieldName: 'external_id', valueRange: t('importShipments.docExternalId'), exampleValue: 'S-001', required: false },
      { id: 1, fieldName: 'shipper_reference', valueRange: t('importShipments.docVr1'), exampleValue: 'SH12345', required: false },
      // Third-party tracking number is optional; a blank cell is stored as empty.
      { id: 2, fieldName: 'tracking_number', valueRange: t('importShipments.docVr2'), exampleValue: 'TRK987654', required: false },
      { id: 3, fieldName: 'items_description', valueRange: t('importShipments.docVr3'), exampleValue: 'Electronics', required: false },
      { id: 4, fieldName: 'packages_quantity', valueRange: t('importShipments.docVr4'), exampleValue: '5', required: true },
      { id: 5, fieldName: 'weight', valueRange: t('importShipments.docVr5'), exampleValue: '10.5', required: false },
      { id: 6, fieldName: 'width', valueRange: t('importShipments.docVr6'), exampleValue: '30.0', required: false },
      { id: 7, fieldName: 'length', valueRange: t('importShipments.docVr7'), exampleValue: '40.0', required: false },
      { id: 8, fieldName: 'height', valueRange: t('importShipments.docVr8'), exampleValue: '20.0', required: false },
      { id: 9, fieldName: 'volumetric_weight', valueRange: t('importShipments.docVr9'), exampleValue: '25.0', required: true },
      { id: 10, fieldName: 'charge', valueRange: t('importShipments.docVr10'), exampleValue: '150.75', required: false },
      { id: 11, fieldName: 'price', valueRange: t('importShipments.docVr11'), exampleValue: '150.75', required: false },
      { id: 12, fieldName: 'auto_calculating_price', valueRange: t('importShipments.docVr12'), exampleValue: '1', required: true },
      { id: 13, fieldName: 'owner_id', valueRange: t('importShipments.docVr13'), exampleValue: '1', required: false },
      { id: 14, fieldName: 'location', valueRange: t('importShipments.docVr14'), exampleValue: '1', required: true },
      { id: 15, fieldName: 'current_location', valueRange: t('importShipments.docCurrentLocation'), exampleValue: 'Warehouse A', required: true },
      { id: 16, fieldName: 'note', valueRange: t('importShipments.docVr15'), exampleValue: 'Handle with care', required: false },
      { id: 17, fieldName: 'transaction_date', valueRange: t('importShipments.docVr16'), exampleValue: '2024-01-15', required: false },
      { id: 18, fieldName: 'status', valueRange: t('importShipments.docVr17'), exampleValue: 'delivered', required: true },
      { id: 19, fieldName: 'paid', valueRange: t('importShipments.docPaid'), exampleValue: '0', required: true },
      { id: 20, fieldName: 'payment_status', valueRange: t('importShipments.docPaymentStatus'), exampleValue: 'unpaid', required: true },
      { id: 21, fieldName: 'type', valueRange: t('importShipments.docVr19'), exampleValue: '1', required: false },
      { id: 22, fieldName: 'shipment_unit_number', valueRange: t('importShipments.docShipmentUnitNumber'), exampleValue: 'SU-2026-042', required: false },
      { id: 23, fieldName: 'pricing_tariff_code', valueRange: t('importShipments.docPricingTariff'), exampleValue: 'T1', required: false },
      { id: 24, fieldName: 'product_category_code', valueRange: t('importShipments.docProductCategory'), exampleValue: 'normal', required: false },
      { id: 25, fieldName: 'meter_price_override', valueRange: t('importShipments.docMeterOverride'), exampleValue: '30.000', required: false },
      { id: 26, fieldName: 'extra_cost', valueRange: t('importShipments.docExtraCost'), exampleValue: '0.500', required: false },
      { id: 27, fieldName: 'tags', valueRange: t('importShipments.docVr21'), exampleValue: 'vip, urgent', required: false },
    ],
    [t]
  );

  const piecesStructureRows = useMemo(
    () => [
      { id: 1, fieldName: 'external_id', valueRange: t('importShipments.piecesDocExternalId'), exampleValue: 'S-003', required: true },
      { id: 2, fieldName: 'sequence', valueRange: t('importShipments.piecesDocSequence'), exampleValue: '1', required: false },
      { id: 3, fieldName: 'description', valueRange: t('importShipments.piecesDocDescription'), exampleValue: 'Box A', required: false },
      { id: 4, fieldName: 'weight_kg', valueRange: t('importShipments.piecesDocWeightKg'), exampleValue: '1.5', required: false },
      { id: 5, fieldName: 'width_cm', valueRange: t('importShipments.piecesDocDimsCm'), exampleValue: '30', required: false },
      { id: 6, fieldName: 'length_cm', valueRange: t('importShipments.piecesDocDimsCm'), exampleValue: '40', required: false },
      { id: 7, fieldName: 'height_cm', valueRange: t('importShipments.piecesDocDimsCm'), exampleValue: '20', required: false },
      { id: 8, fieldName: 'volumetric_weight_kg', valueRange: t('importShipments.piecesDocVolKg'), exampleValue: '5.0', required: false },
      { id: 9, fieldName: 'product_category_code', valueRange: t('importShipments.piecesDocCategory'), exampleValue: 'normal', required: false },
      { id: 10, fieldName: 'meter_price_override', valueRange: t('importShipments.piecesDocOverride'), exampleValue: '30.000', required: false },
      { id: 11, fieldName: 'extra_cost', valueRange: t('importShipments.piecesDocExtraCost'), exampleValue: '0.500', required: false },
      { id: 12, fieldName: 'note', valueRange: t('importShipments.piecesDocNote'), exampleValue: 'Fragile', required: false },
    ],
    [t]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, px: 3 }}>
          <Box mb={4}>
            <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
              {[t('importShipments.breadcrumb1'), t('importShipments.breadcrumb2')].map((item) => (
                <Link
                  key={item}
                  color="neutral"
                  href="/shipments"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': { textDecoration: 'none' },
                  }}
                >
                  {item}
                </Link>
              ))}
            </Breadcrumbs>
            <Typography
              textAlign="start"
              level="h1"
              sx={{ mt: 2, fontSize: '2rem', fontWeight: 600, color: textPrimary }}
            >
              {t('importShipments.pageTitle')}
            </Typography>
          </Box>

          {/* Step 1: Upload */}
          <Card
            variant="plain"
            sx={{
              bgcolor: cardBg,
              boxShadow: cardShadow,
              borderRadius: cardRadius,
              border: cardBorder,
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography level="title-md" sx={{ mb: 2, color: textPrimary, fontWeight: 600 }}>
                {t('importShipments.step1Title')}
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                <Button
                  variant="soft"
                  size="md"
                  startDecorator={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  loading={templateLoading}
                  disabled={templateLoading || sampleLoading}
                  sx={{ borderRadius: '10px', fontWeight: 500 }}
                >
                  {t('importShipments.downloadTemplate')}
                </Button>
                <Button
                  variant="outlined"
                  size="md"
                  startDecorator={<DownloadIcon />}
                  onClick={handleDownloadSample}
                  loading={sampleLoading}
                  disabled={templateLoading || sampleLoading}
                  sx={{ borderRadius: '10px', fontWeight: 500 }}
                >
                  {t('importShipments.downloadSample')}
                </Button>
              </Box>
              <Typography level="body2" sx={{ color: textSecondary, mb: 1 }}>
                {t('importShipments.introParagraph')}
              </Typography>
              <Alert
                variant="soft"
                color="primary"
                size="sm"
                startDecorator={<Inventory2Icon />}
                sx={{ mb: 2, borderRadius: '10px' }}
              >
                <Typography level="body2">{t('importShipments.piecesLegend')}</Typography>
              </Alert>
              <Alert
                variant="soft"
                color="neutral"
                size="sm"
                startDecorator={<InfoIcon />}
                sx={{ mb: 2, borderRadius: '10px' }}
              >
                <Typography level="body2">{t('importShipments.shipmentUnitHint')}</Typography>
              </Alert>
              {templateError && (
                <Alert color="danger" variant="soft" size="sm" sx={{ mb: 2, borderRadius: '10px' }}>
                  {templateError}
                </Alert>
              )}
              {sampleError && (
                <Alert color="danger" variant="soft" size="sm" sx={{ mb: 2, borderRadius: '10px' }}>
                  {sampleError}
                </Alert>
              )}
              <Box
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                role="region"
                aria-label={t('importShipments.dropZoneAria')}
                sx={{
                  border: '2px dashed',
                  borderColor: dragActive ? '#3b82f6' : '#cbd5e1',
                  borderRadius: cardRadius,
                  bgcolor: dragActive ? 'rgba(59, 130, 246, 0.06)' : 'rgba(248, 250, 252, 0.9)',
                  p: 2.5,
                  mb: 2,
                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    textAlign: 'center',
                  }}
                >
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: dragActive ? '#3b82f6' : textSecondary }}
                    aria-hidden
                  />
                  <Typography level="body2" sx={{ color: textSecondary, maxWidth: 520 }}>
                    {t('importShipments.dropHint')}
                  </Typography>
                  {file && (
                    <Typography level="body2" sx={{ color: textPrimary, fontWeight: 600 }}>
                      {t('importShipments.selectedFile', { name: file.name })}
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <input
                      ref={fileInputRef}
                      key={inputKey}
                      type="file"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        width: '100%',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ borderRadius: '10px', fontWeight: 500 }}
                      >
                        {t('importShipments.fileInputPlaceholder')}
                      </Button>
                      <Typography
                        level="body2"
                        sx={{
                          flex: 1,
                          minWidth: 140,
                          color: file ? textPrimary : textSecondary,
                          fontStyle: file ? 'normal' : 'italic',
                        }}
                      >
                        {file ? file.name : t('importShipments.noFileSelected')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="button"
                      onClick={handleUploadPreview}
                      loading={uploadLoading}
                      disabled={!file || uploadLoading}
                      startDecorator={<CloudUploadIcon />}
                      size="lg"
                      color="primary"
                      variant="solid"
                      sx={{ width: { xs: '100%', sm: 'auto' }, borderRadius: '10px' }}
                    >
                      {t('importShipments.validatePreview')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Global errors */}
          {globalErrors.length > 0 && (
            <Alert
              variant="soft"
              color="danger"
              startDecorator={<ErrorIcon />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Box>
                {globalErrors.map((msg, i) => (
                  <Typography key={i} level="body2">
                    {msg}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          {/* Blocking shipment-row errors (excluded from preview). */}
          {step === 'preview' && classifiedRowErrors.shipmentErrors.length > 0 && (
            <Alert
              variant="soft"
              color="danger"
              startDecorator={<ErrorIcon />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  {t('importShipments.rowsWithErrorsTitle')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {classifiedRowErrors.shipmentErrors.map(({ rowIndex, message }) => (
                    <li key={`ship-err-${rowIndex}`}>
                      <Typography level="body2">
                        {t('importShipments.rowErrorLine', {
                          n: Number.isFinite(rowIndex) ? rowIndex + 1 : rowIndex,
                          msg: message,
                        })}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Alert>
          )}

          {/* Blocking piece-row errors (parent shipment is still imported, but this piece is dropped). */}
          {step === 'preview' && classifiedRowErrors.pieceErrors.length > 0 && (
            <Alert
              variant="soft"
              color="danger"
              startDecorator={<Inventory2Icon />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  {t('importShipments.piecesErrorsTitle')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {classifiedRowErrors.pieceErrors.map(({ rowIndex, message }) => (
                    <li key={`piece-err-${rowIndex}`}>
                      <Typography level="body2">
                        {t('importShipments.pieceErrorLine', {
                          n: Number.isFinite(rowIndex) ? rowIndex + 1 : rowIndex,
                          msg: message,
                        })}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Alert>
          )}

          {/* Cross-sheet orphan errors: Pieces row references a missing Shipments external_id. */}
          {step === 'preview' && classifiedRowErrors.orphans.length > 0 && (
            <Alert
              variant="soft"
              color="danger"
              startDecorator={<LinkOffIcon />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  {t('importShipments.orphansTitle')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {classifiedRowErrors.orphans.map(({ externalId, message }) => (
                    <li key={`orphan-${externalId}`}>
                      <Typography level="body2">
                        {t('importShipments.orphanLine', {
                          externalId,
                          msg: message,
                        })}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Alert>
          )}

          {/* Non-blocking warnings (e.g. shipment vs sum-of-pieces weight mismatch). */}
          {step === 'preview' && classifiedRowErrors.warnings.length > 0 && (
            <Alert
              variant="soft"
              color="warning"
              startDecorator={<WarningAmberIcon />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  {t('importShipments.warningsTitle')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {classifiedRowErrors.warnings.map(({ rowIndex, message }) => (
                    <li key={`warn-${rowIndex}`}>
                      <Typography level="body2">
                        {t('importShipments.warningLine', {
                          n: Number.isFinite(rowIndex) ? rowIndex + 1 : rowIndex,
                          msg: message,
                        })}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Alert>
          )}

          {/* Step 2: Preview table & confirm */}
          {step === 'preview' && preview.length > 0 && (
            <>
              <Card
                variant="plain"
                sx={{
                  bgcolor: cardBg,
                  boxShadow: cardShadow,
                  borderRadius: cardRadius,
                  border: cardBorder,
                  mb: 3,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                      {t('importShipments.step2Title')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="soft"
                        size="sm"
                        startDecorator={<DoneAllIcon />}
                        onClick={toggleSelectAll}
                        sx={{ borderRadius: '10px' }}
                      >
                        {allSelected ? t('importShipments.deselectAll') : t('importShipments.selectAll')}
                      </Button>
                      {expandableRowIndices.length > 0 && (
                        <Button
                          variant="soft"
                          size="sm"
                          startDecorator={allExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={toggleExpandAll}
                          sx={{ borderRadius: '10px' }}
                        >
                          {allExpanded
                            ? t('importShipments.collapseAllPieces')
                            : t('importShipments.expandAllPieces', { count: expandableRowIndices.length })}
                        </Button>
                      )}
                      <PermissionGate permission={PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL}>
                        <Button
                          variant="solid"
                          color="primary"
                          size="sm"
                          startDecorator={<PlaylistAddCheckIcon />}
                          onClick={handleConfirmImport}
                          loading={confirmLoading}
                          disabled={!someSelected || confirmLoading}
                          sx={{ borderRadius: '10px', fontWeight: 500 }}
                        >
                          {t('importShipments.importSelected', { count: selectedRowIndices.size })}
                        </Button>
                      </PermissionGate>
                    </Box>
                  </Box>
                  <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                    {t('importShipments.previewHint', { count: preview.length })}
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: '65vh',
                      overflow: 'auto',
                      borderRadius: cardRadius,
                      border: cardBorder,
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    <Sheet
                      variant="outlined"
                      sx={{
                        borderRadius: cardRadius,
                        border: 'none',
                        boxShadow: cardShadow,
                        display: 'inline-block',
                        minWidth: '100%',
                        '& thead th': { fontWeight: 600, color: textSecondary, fontSize: '0.8125rem', whiteSpace: 'nowrap' },
                        '& tbody tr:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        '& td': { verticalAlign: 'middle', fontSize: '0.875rem' },
                        /* Sticky checkbox column: pins to inline-start (high z-index so RTL paint order does not steal clicks) */
                        '& thead th:first-of-type': {
                          position: 'sticky',
                          top: 0,
                          insetInlineStart: 0,
                          zIndex: 12,
                          minWidth: 48,
                          width: 48,
                          backgroundColor: cardBg,
                          backgroundClip: 'padding-box',
                          boxShadow: '4px 0 8px rgba(0,0,0,0.08)',
                          pointerEvents: 'auto',
                          isolation: 'isolate',
                        },
                        'html[dir="rtl"] & thead th:first-of-type': {
                          boxShadow: '-4px 0 8px rgba(0,0,0,0.08)',
                        },
                        '& tbody td:first-of-type': {
                          position: 'sticky',
                          insetInlineStart: 0,
                          zIndex: 11,
                          backgroundColor: cardBg,
                          backgroundClip: 'padding-box',
                          boxShadow: '4px 0 8px rgba(0,0,0,0.06)',
                          pointerEvents: 'auto',
                          isolation: 'isolate',
                        },
                        'html[dir="rtl"] & tbody td:first-of-type': {
                          boxShadow: '-4px 0 8px rgba(0,0,0,0.06)',
                        },
                        '& tbody tr:hover td:first-of-type': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <Table stickyHeader hoverRow size="md" sx={{ minWidth: 800, borderCollapse: 'separate', borderSpacing: 0 }}>
                        {(() => {
                          const dynamicColumns = getPreviewColumns(preview);
                          const totalColSpan = 2 + dynamicColumns.length + 1; // checkbox + row + cols + pieces
                          return (
                            <>
                              <thead>
                                <tr>
                                  <th style={{ width: 48, minWidth: 48 }}>
                                    <NativeCheckbox
                                      checked={allSelected}
                                      indeterminate={someSelected && !allSelected}
                                      onChange={toggleSelectAll}
                                      sx={{ verticalAlign: 'middle' }}
                                    />
                                  </th>
                                  <th style={{ width: 56, minWidth: 56 }}>{t('importShipments.thRow')}</th>
                                  {dynamicColumns.map((key) => {
                                    const { minWidth, width } = previewColumnWidth(key);
                                    return (
                                      <th key={key} style={{ minWidth, width }}>
                                        {previewColumnLabel(key, t)}
                                      </th>
                                    );
                                  })}
                                  <th style={{ minWidth: 120, width: 140 }}>
                                    {t('importShipments.previewCol.pieces')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview.map((row) => {
                                  const statusStyle = shipmentStatusChipStyle(row.status);
                                  const pieces = Array.isArray(row.pieces) ? row.pieces : [];
                                  const hasPieces = pieces.length > 0;
                                  const isExpanded = hasPieces && expandedRowIndices.has(row.row_index);
                                  const warningMsg = warningByShipmentRow.get(row.row_index);
                                  return (
                                    <React.Fragment key={`row-${row.row_index}`}>
                                      <tr>
                                        <td>
                                          <NativeCheckbox
                                            checked={selectedRowIndices.has(row.row_index)}
                                            onChange={() => toggleSelectRow(row.row_index)}
                                            sx={{ verticalAlign: 'middle' }}
                                          />
                                        </td>
                                        <td>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                                              {row.row_index + 1}
                                            </Typography>
                                            {warningMsg && (
                                              <Tooltip
                                                title={warningMsg}
                                                placement="top"
                                                variant="soft"
                                                color="warning"
                                              >
                                                <WarningAmberIcon
                                                  fontSize="small"
                                                  aria-label={t('importShipments.warningBadge')}
                                                  sx={{ color: '#d97706', cursor: 'help' }}
                                                />
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </td>
                                        {dynamicColumns.map((key) => {
                                          const value = row[key];
                                          const display = formatPreviewCellValue(key, value, row, t);
                                          const isLongTextTruncate =
                                            key === 'items_description' ||
                                            key === 'owner_id' ||
                                            key === 'location_id' ||
                                            key === 'location' ||
                                            key === 'type_id' ||
                                            key === 'type';
                                          return (
                                            <td key={key}>
                                              {key === 'status' && value != null && value !== '' ? (
                                                <Chip
                                                  size="sm"
                                                  variant="soft"
                                                  sx={{
                                                    bgcolor: statusStyle.bg,
                                                    color: statusStyle.color,
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                  }}
                                                >
                                                  {display}
                                                </Chip>
                                              ) : key === 'payment_status' && value != null && value !== '' ? (
                                                <Chip
                                                  size="sm"
                                                  variant="soft"
                                                  sx={{
                                                    ...getPaymentStatusChipStyle(value),
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                  }}
                                                >
                                                  {display}
                                                </Chip>
                                              ) : key === 'external_id' && value != null && value !== '' ? (
                                                <Chip size="sm" variant="soft" color="primary" sx={{ fontFamily: 'monospace' }}>
                                                  {display}
                                                </Chip>
                                              ) : key === 'service_ids' ? (
                                                <Typography
                                                  level="body2"
                                                  sx={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    maxWidth: 260,
                                                    lineHeight: 1.4,
                                                  }}
                                                >
                                                  {display}
                                                </Typography>
                                              ) : key === 'tag_names' ? (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                    alignItems: 'center',
                                                    maxWidth: 220,
                                                  }}
                                                >
                                                  {!Array.isArray(value) || value.length === 0 ? (
                                                    <Typography level="body2">—</Typography>
                                                  ) : (
                                                    value
                                                      .filter((n) => n != null && String(n).trim() !== '')
                                                      .map((name) => (
                                                        <Chip key={`${row.row_index}-${name}`} size="sm" variant="soft" color="neutral">
                                                          {name}
                                                        </Chip>
                                                      ))
                                                  )}
                                                </Box>
                                              ) : key === 'shipment_unit_number' ? (
                                                value != null && value !== '' ? (
                                                  <Box sx={{ maxWidth: 160 }}>
                                                    <Typography level="body2" noWrap title={String(value)}>
                                                      {String(value)}
                                                    </Typography>
                                                    {row.shipment_unit_serial_number != null &&
                                                      row.shipment_unit_serial_number !== '' && (
                                                        <Tooltip
                                                          title={t('importShipments.shipmentUnitSerialTooltip', {
                                                            serial: row.shipment_unit_serial_number,
                                                          })}
                                                          placement="top"
                                                          variant="soft"
                                                        >
                                                          <Typography
                                                            level="body3"
                                                            sx={{ color: textSecondary, cursor: 'help' }}
                                                            noWrap
                                                          >
                                                            {t('importShipments.shipmentUnitSerialLabel', {
                                                              serial: row.shipment_unit_serial_number,
                                                            })}
                                                          </Typography>
                                                        </Tooltip>
                                                      )}
                                                  </Box>
                                                ) : (
                                                  '—'
                                                )
                                              ) : isLongTextTruncate ? (
                                                <Typography
                                                  level="body2"
                                                  sx={{ maxWidth: 180 }}
                                                  noWrap
                                                  title={display}
                                                >
                                                  {display}
                                                </Typography>
                                              ) : (
                                                display
                                              )}
                                            </td>
                                          );
                                        })}
                                        <td>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {hasPieces ? (
                                              <>
                                                <Chip size="sm" variant="soft" color="primary">
                                                  {t('importShipments.piecesCountBadge', { count: pieces.length })}
                                                </Chip>
                                                <Tooltip
                                                  title={
                                                    isExpanded
                                                      ? t('importShipments.collapsePiecesTooltip')
                                                      : t('importShipments.expandPiecesTooltip')
                                                  }
                                                  placement="top"
                                                  variant="soft"
                                                >
                                                  <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    aria-label={
                                                      isExpanded
                                                        ? t('importShipments.collapsePiecesTooltip')
                                                        : t('importShipments.expandPiecesTooltip')
                                                    }
                                                    onClick={() => toggleExpandRow(row.row_index)}
                                                  >
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                  </IconButton>
                                                </Tooltip>
                                              </>
                                            ) : (
                                              <Tooltip
                                                title={t('importShipments.piecesAutoCreateTooltip')}
                                                placement="top"
                                                variant="soft"
                                              >
                                                <Chip size="sm" variant="soft" color="neutral">
                                                  {t('importShipments.piecesAutoCreateBadge', {
                                                    count: row.packages_quantity != null ? row.packages_quantity : 0,
                                                  })}
                                                </Chip>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </td>
                                      </tr>
                                      {isExpanded && (
                                        <tr key={`pieces-${row.row_index}`}>
                                          <td colSpan={totalColSpan} style={{ padding: 0, backgroundColor: '#f8fafc' }}>
                                            <Box sx={{ p: 2 }}>
                                              <Typography level="title-sm" sx={{ mb: 1, color: textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Inventory2Icon fontSize="small" />
                                                {t('importShipments.piecesSubRowTitle', { count: pieces.length })}
                                              </Typography>
                                              <Box sx={{ overflowX: 'auto' }}>
                                                <Table
                                                  size="sm"
                                                  variant="outlined"
                                                  sx={{
                                                    backgroundColor: cardBg,
                                                    borderRadius: '10px',
                                                    minWidth: 760,
                                                    '& thead th': {
                                                      fontWeight: 600,
                                                      color: textSecondary,
                                                      fontSize: '0.75rem',
                                                      textTransform: 'uppercase',
                                                      letterSpacing: '0.04em',
                                                    },
                                                    '& tbody td': {
                                                      fontSize: '0.8125rem',
                                                    },
                                                  }}
                                                >
                                                  <thead>
                                                    <tr>
                                                      <th style={{ width: 48 }}>{t('importShipments.piecesHeader.seq')}</th>
                                                      <th>{t('importShipments.piecesHeader.description')}</th>
                                                      <th>{t('importShipments.piecesHeader.weightKg')}</th>
                                                      <th>{t('importShipments.piecesHeader.dims')}</th>
                                                      <th>{t('importShipments.piecesHeader.volKg')}</th>
                                                      <th>{t('importShipments.piecesHeader.category')}</th>
                                                      <th>{t('importShipments.piecesHeader.override')}</th>
                                                      <th>{t('importShipments.piecesHeader.extraCost')}</th>
                                                      <th>{t('importShipments.piecesHeader.note')}</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {pieces.map((p, idx) => {
                                                      const dims =
                                                        p.width_cm != null && p.length_cm != null && p.height_cm != null
                                                          ? `${formatNumeric(p.width_cm)}×${formatNumeric(p.length_cm)}×${formatNumeric(p.height_cm)}`
                                                          : '—';
                                                      return (
                                                        <tr key={`p-${row.row_index}-${idx}`}>
                                                          <td>
                                                            <Typography level="body2" sx={{ fontWeight: 600 }}>
                                                              {p.sequence != null ? p.sequence : idx + 1}
                                                            </Typography>
                                                          </td>
                                                          <td>
                                                            <Typography level="body2" sx={{ maxWidth: 200 }} noWrap title={p.description || ''}>
                                                              {p.description != null && p.description !== '' ? p.description : '—'}
                                                            </Typography>
                                                          </td>
                                                          <td>{formatNumeric(p.weight_kg)}</td>
                                                          <td>{dims}</td>
                                                          <td>
                                                            {p.volumetric_weight_kg != null
                                                              ? formatNumeric(p.volumetric_weight_kg)
                                                              : (
                                                                <Tooltip title={t('importShipments.piecesAutoVolTooltip')} variant="soft">
                                                                  <Chip size="sm" variant="soft" color="neutral">
                                                                    {t('importShipments.piecesAutoVolBadge')}
                                                                  </Chip>
                                                                </Tooltip>
                                                              )}
                                                          </td>
                                                          <td>
                                                            {p.product_category_code ? (
                                                              <Chip size="sm" variant="soft" color="primary">
                                                                {p.product_category_code}
                                                              </Chip>
                                                            ) : (
                                                              '—'
                                                            )}
                                                          </td>
                                                          <td>
                                                            {p.meter_price_override != null ? (
                                                              <Chip size="sm" variant="soft" color="warning">
                                                                {formatNumeric(p.meter_price_override)}
                                                              </Chip>
                                                            ) : (
                                                              '—'
                                                            )}
                                                          </td>
                                                          <td>
                                                            {p.extra_cost != null && Number(p.extra_cost) !== 0
                                                              ? formatNumeric(p.extra_cost)
                                                              : '—'}
                                                          </td>
                                                          <td>
                                                            <Typography level="body2" sx={{ maxWidth: 200 }} noWrap title={p.note || ''}>
                                                              {p.note != null && p.note !== '' ? p.note : '—'}
                                                            </Typography>
                                                          </td>
                                                        </tr>
                                                      );
                                                    })}
                                                  </tbody>
                                                </Table>
                                              </Box>
                                              <Typography level="body3" sx={{ mt: 1, color: textSecondary, fontStyle: 'italic' }}>
                                                {t('importShipments.piecesReadOnlyHint')}
                                              </Typography>
                                            </Box>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </>
                          );
                        })()}
                      </Table>
                  </Sheet>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}

          {/* Confirm result */}
          {confirmResult && (
            <Alert
              variant="soft"
              color={confirmResult.errors && confirmResult.errors.length > 0 ? 'warning' : 'success'}
              startDecorator={confirmResult.created_count > 0 ? <CheckCircleIcon /> : <InfoIcon />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <Box>
                <Typography level="body1" sx={{ fontWeight: 600 }}>
                  {confirmResult.detail}
                </Typography>
                {confirmResult.errors && confirmResult.errors.length > 0 && (
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    {confirmResult.errors.map((e, i) => (
                      <li key={i}>
                        <Typography level="body2">
                          {t('importShipments.confirmErrorRow', {
                            n: (e.row_index != null ? e.row_index : e) + 1,
                            msg: typeof e === 'object' && e.error ? e.error : String(e),
                          })}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
                {confirmResult.created_count > 0 ? (
                  <PermissionGate
                    permission={PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL}
                    fallback={
                      <Button variant="soft" size="sm" sx={{ mt: 1 }} onClick={startOver}>
                        {t('importShipments.importAnotherFile')}
                      </Button>
                    }
                  >
                    <Button
                      variant="soft"
                      size="sm"
                      component={Link}
                      href="/shipments"
                      sx={{ mt: 1 }}
                    >
                      {t('importShipments.moveToShipmentsList')}
                    </Button>
                  </PermissionGate>
                ) : (
                  <Button variant="soft" size="sm" sx={{ mt: 1 }} onClick={startOver}>
                    {t('importShipments.importAnotherFile')}
                  </Button>
                )}
              </Box>
            </Alert>
          )}

          {/* Shipments sheet structure */}
          <Card
            variant="plain"
            sx={{
              p: 3,
              borderRadius: cardRadius,
              backgroundColor: cardBg,
              border: cardBorder,
              boxShadow: cardShadow,
              mb: 3,
            }}
          >
            <Typography level="h4" startDecorator={<InfoIcon />} sx={{ mb: 1, color: textPrimary }}>
              {t('importShipments.excelStructureTitle')}
            </Typography>
            <Typography level="body1" sx={{ mb: 2, color: textSecondary }}>
              {t('importShipments.excelStructureIntro')}
            </Typography>
            <Chip size="sm" variant="soft" color="primary" sx={{ mb: 2 }}>
              {t('importShipments.shipmentsSheetTab')}
            </Chip>
            <DataGrid
              rows={structureRows}
              columns={[
                { field: 'fieldName', headerName: t('importShipments.dgFieldName'), flex: 2, minWidth: 180, headerClassName: 'super-app-theme--header' },
                { field: 'valueRange', headerName: t('importShipments.dgValueRange'), flex: 3, minWidth: 200, headerClassName: 'super-app-theme--header' },
                { field: 'exampleValue', headerName: t('importShipments.dgExampleValue'), flex: 2, minWidth: 150, headerClassName: 'super-app-theme--header' },
                {
                  field: 'required',
                  headerName: t('importShipments.dgRequired'),
                  flex: 1,
                  minWidth: 100,
                  headerClassName: 'super-app-theme--header',
                  renderCell: (params) =>
                    params.value ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />,
                },
              ]}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  lineHeight: 'normal',
                  padding: '12px 8px',
                },
                '& .super-app-theme--header': {
                  backgroundColor: 'primary.softBg',
                  fontWeight: 'bold',
                },
                '@media (max-width: 600px)': {
                  '& .MuiDataGrid-toolbarContainer': { flexDirection: 'column', gap: 1 },
                  '& .MuiDataGrid-footer': { flexDirection: 'column', gap: 1 },
                  '& .MuiDataGrid-cell': { minHeight: '60px', alignItems: 'center', padding: '8px' },
                },
              }}
            />
          </Card>

          {/* Pieces sheet structure (optional second tab) */}
          <Card
            variant="plain"
            sx={{
              p: 3,
              borderRadius: cardRadius,
              backgroundColor: cardBg,
              border: cardBorder,
              boxShadow: cardShadow,
            }}
          >
            <Typography level="h4" startDecorator={<Inventory2Icon />} sx={{ mb: 1, color: textPrimary }}>
              {t('importShipments.piecesStructureTitle')}
            </Typography>
            <Typography level="body1" sx={{ mb: 2, color: textSecondary }}>
              {t('importShipments.piecesStructureIntro')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip size="sm" variant="soft" color="primary">
                {t('importShipments.piecesSheetTab')}
              </Chip>
              <Chip size="sm" variant="soft" color="neutral">
                {t('importShipments.piecesSheetOptional')}
              </Chip>
            </Box>
            <DataGrid
              rows={piecesStructureRows}
              columns={[
                { field: 'fieldName', headerName: t('importShipments.dgFieldName'), flex: 2, minWidth: 180, headerClassName: 'super-app-theme--header' },
                { field: 'valueRange', headerName: t('importShipments.dgValueRange'), flex: 3, minWidth: 200, headerClassName: 'super-app-theme--header' },
                { field: 'exampleValue', headerName: t('importShipments.dgExampleValue'), flex: 2, minWidth: 150, headerClassName: 'super-app-theme--header' },
                {
                  field: 'required',
                  headerName: t('importShipments.dgRequired'),
                  flex: 1,
                  minWidth: 100,
                  headerClassName: 'super-app-theme--header',
                  renderCell: (params) =>
                    params.value ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />,
                },
              ]}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  lineHeight: 'normal',
                  padding: '12px 8px',
                },
                '& .super-app-theme--header': {
                  backgroundColor: 'primary.softBg',
                  fontWeight: 'bold',
                },
                '@media (max-width: 600px)': {
                  '& .MuiDataGrid-toolbarContainer': { flexDirection: 'column', gap: 1 },
                  '& .MuiDataGrid-footer': { flexDirection: 'column', gap: 1 },
                  '& .MuiDataGrid-cell': { minHeight: '60px', alignItems: 'center', padding: '8px' },
                },
              }}
            />
            <Alert
              variant="soft"
              color="neutral"
              size="sm"
              startDecorator={<InfoIcon />}
              sx={{ mt: 2, borderRadius: '10px' }}
            >
              <Typography level="body2">{t('importShipments.piecesFallbackHint')}</Typography>
            </Alert>
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
